import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Crown, AlertOctagon, X, Sparkles, ArrowRight, Camera } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useSubscription } from "@/hooks/useSubscription";
import { supabase } from "@/integrations/supabase/client";

export function AssignedNotificationPopup() {
  const [isOpen, setIsOpen] = useState(false);
  const [latestAlbumId, setLatestAlbumId] = useState<string | null>(null);
  const { user } = useAuth();
  const { isSubscriber } = useSubscription();
  const navigate = useNavigate();

  useEffect(() => {
    if (!user) return;
    
    // Check if we should show it today
    const lastSeen = localStorage.getItem(`assigned_popup_seen_${user.id}`);
    const today = new Date().toDateString();
    
    if (lastSeen === today) {
      return; // Already seen today
    }

    const checkAssignedPhotos = async () => {
      try {
        let orQuery = `assigned_to_user_id.eq.${user.id}`;
        if (isSubscriber) {
          orQuery += `,assigned_to_user_id.eq.ALL_VIP`;
        }

        const { data, error } = await supabase
          .from("album_media")
          .select("album_id, created_at")
          .or(orQuery)
          .order("created_at", { ascending: false })
          .limit(1);

        if (!error && data && data.length > 0) {
          setLatestAlbumId(data[0].album_id);
          setIsOpen(true);
        }
      } catch (e) {
        console.error("Erro ao checar fotos atribuidas:", e);
      }
    };

    checkAssignedPhotos();
  }, [user, isSubscriber]);

  const handleClose = () => {
    if (user) {
      localStorage.setItem(`assigned_popup_seen_${user.id}`, new Date().toDateString());
    }
    setIsOpen(false);
  };

  const handleGoToPhotos = () => {
    if (user) {
      localStorage.setItem(`assigned_popup_seen_${user.id}`, new Date().toDateString());
    }
    setIsOpen(false);
    if (latestAlbumId) {
      navigate(`/albums/${latestAlbumId}?tab=assigned`);
    } else {
      navigate("/albums");
    }
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <div className="fixed inset-0 z-50 flex items-center justify-center px-4 bg-black/60 backdrop-blur-sm">
        <motion.div
          initial={{ scale: 0.9, opacity: 0, y: 20 }}
          animate={{ scale: 1, opacity: 1, y: 0 }}
          exit={{ scale: 0.9, opacity: 0, y: 20 }}
          className="relative bg-foreground/5 rounded-3xl overflow-hidden border border-amber-500/30 shadow-[0_0_50px_rgba(245,158,11,0.2)] w-full max-w-md"
        >
          {/* Animated Glow */}
          <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[200%] h-[200px] bg-gradient-to-b from-amber-500/20 to-transparent blur-[60px] pointer-events-none" />

          {/* Close Button */}
          <button 
            onClick={handleClose}
            className="absolute top-4 right-4 z-50 p-2 bg-black/50 hover:bg-white/10 rounded-full transition-colors text-white/50 hover:text-white"
          >
            <X className="w-5 h-5" />
          </button>

          <div className="p-8 flex flex-col items-center text-center relative z-10">
            <div className="w-20 h-20 bg-gradient-to-br from-amber-400 to-amber-600 rounded-full flex items-center justify-center mb-6 shadow-[0_0_30px_rgba(245,158,11,0.5)] border-4 border-[#1c1c1e]">
              <Crown className="w-10 h-10 text-black fill-black" />
            </div>

            <h2 className="text-2xl font-black text-white uppercase tracking-tight mb-2">
              Você tem Fotos Exclusivas!
            </h2>
            
            <p className="text-gray-400 mb-6 max-w-sm font-medium text-sm">
              Novas fotos foram separadas especialmente para você na aba de assinantes.
            </p>

            <div className="bg-black/50 border border-amber-500/30 rounded-2xl p-5 w-full mb-8 relative overflow-hidden group shadow-[inset_0_0_20px_rgba(245,158,11,0.05)]">
              <div className="absolute inset-0 bg-gradient-to-r from-amber-500 to-yellow-400 opacity-5 group-hover:opacity-10 transition-opacity" />
              <div className="flex justify-center mb-3">
                <Camera className="w-8 h-8 text-amber-500" />
              </div>
              <p className="text-xs font-bold text-amber-400/90 uppercase tracking-wider leading-relaxed">
                Clique abaixo para ir direto para a sua pasta reservada e conferir os registros.
              </p>
            </div>

            <Button
              onClick={handleGoToPhotos}
              className="w-full h-14 bg-gradient-to-r from-amber-500 to-yellow-500 hover:from-amber-600 hover:to-yellow-600 text-black font-black uppercase tracking-widest text-sm rounded-xl flex items-center justify-center gap-3 transition-transform hover:scale-105 shadow-[0_0_20px_rgba(245,158,11,0.4)]"
            >
              VER MINHAS FOTOS
              <ArrowRight className="w-5 h-5" />
            </Button>
          </div>
        </motion.div>
      </div>
    </AnimatePresence>
  );
}
